import { Currency } from '@models/Currency'
import { currencySchema } from '@models/schema'
import { createSelector, createSlice } from '@reduxjs/toolkit'
import { fetchCurrencies, fetchCurrencyById } from '@services/CurrencyService'
import { AppDispatch, RootState } from '@store/store'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'

const initialState: SBState<Currency> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'currency',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getCurrenciesSuccess,
  setQuery: setCurrencyQuery,
  reset: resetCurrencyState,
  resetQueryAndIds: resetCurrencyQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<RootState, { [key: number]: Currency }> = (
  state: RootState
) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectCurrencies = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedCurrency = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectCurrencyById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectCurrenciesByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )
/**
 * Actions
 */

export const setSelectedCurrency =
  (currency: Currency | number) => async (dispatch: AppDispatch) =>
    dispatch(slice.actions.setSelectedId(getIdOrModelId<Currency>(currency)))

export const getCurrencies = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchCurrencies(params),
    [currencySchema],
    slice.actions
  )
export const getCurrencyWithId = (id: number) =>
  SBAPI.FetchDispatched<Currency>(
    fetchCurrencyById(id),
    currencySchema,
    slice.actions
  )
